import { zodResolver } from '@hookform/resolvers/zod';
import Grid from '@mui/material/Grid2';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { EnquiryFormShell, HoneypotField, PhoneInputField, TextInputField } from '@/components/forms';
import { type AccessoryOrderEntry } from '@/hooks/useAccessoryOrderItems';
import { useSubmitAccessoryOrder } from '@/hooks/useSubmitAccessoryOrder';
import {
  accessoryOrderFormDefaults,
  accessoryOrderFormSchema,
  type AccessoryOrderFormValues,
} from '@/schemas/enquiry';
import { useAccessoryOrderStore } from '@/stores/accessoryOrderStore';

import { orderableEntries, orderWhatsAppMessage } from './accessoryOrderTotals';

/**
 * Accessory order request. One request covers every part on the list.
 */
export function AccessoryCheckoutForm({ entries }: { entries: AccessoryOrderEntry[] }) {
  const navigate = useNavigate();
  const clear = useAccessoryOrderStore((s) => s.clear);
  const submit = useSubmitAccessoryOrder();
  const { control, register, handleSubmit, watch } = useForm<AccessoryOrderFormValues>({
    resolver: zodResolver(accessoryOrderFormSchema),
    defaultValues: accessoryOrderFormDefaults,
  });

  const parts = orderableEntries(entries);

  const onSubmit = handleSubmit((values) => {
    submit.mutate(
      {
        fullName: values.fullName,
        phone: values.phone,
        email: values.email,
        ...(values.message ? { message: values.message } : {}),
        items: parts.map((p) => ({ slug: p.slug, quantity: p.quantity })),
        pagePath: '/accessories/checkout',
        ...(values.companyWebsite ? { companyWebsite: values.companyWebsite } : {}),
      },
      {
        onSuccess: (receipt) => {
          clear();
          navigate('/accessories/submitted', { state: { reference: receipt.referenceNo } });
        },
      },
    );
  });

  return (
    <EnquiryFormShell
      title="Your details"
      description="Tell us how to reach you and we will send one order covering the parts on your list."
      submitLabel="Submit order request"
      onSubmit={onSubmit}
      isPending={submit.isPending}
      error={submit.error}
      receipt={null}
      whatsappMessage={orderWhatsAppMessage(entries, {
        fullName: watch('fullName'),
        phone: watch('phone'),
        message: watch('message'),
      })}
    >
      <HoneypotField field={register('companyWebsite')} />
      <Grid container spacing={5}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInputField control={control} name="fullName" label="Full name" required autoComplete="name" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PhoneInputField
            control={control}
            name="phone"
            label="WhatsApp number"
            required
            helperText="We'll follow up on this number."
          />
        </Grid>
        <Grid size={12}>
          <TextInputField control={control} name="email" label="Email" required type="email" autoComplete="email" />
        </Grid>
        <Grid size={12}>
          <TextInputField
            control={control}
            name="message"
            label="Anything we should know?"
            multiline
            rows={4}
            placeholder="Fitment, vehicle make/model/year, delivery location…"
          />
        </Grid>
      </Grid>
    </EnquiryFormShell>
  );
}